import { useCallback } from "react"
import { Box, Typography, Stack } from "@mui/material"
import { t } from "@lingui/macro"

import ScreenSearchDesktopRoundedIcon from "@mui/icons-material/ScreenSearchDesktopRounded"

import { useNavigate } from "react-router-dom"

import { ITenantCardProps } from "src/views/contracts/pages/details/types/contract-detail"
import { StyledContractDetails } from "src/views/contracts/pages/details/styled"

import { Avatar } from "src/components/Avatar"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"

export const TenantCard = ({ tenant, onSimulateTenant }: ITenantCardProps) => {
    const navigate = useNavigate()
    const globalStore = useStore(GlobalStore)

    const handleSimulate = useCallback(() => {
        onSimulateTenant(tenant.tenant_id)
    }, [onSimulateTenant, tenant.tenant_id])

    const handleTenantClick = (tenantId: number | undefined) => {
        if (tenantId != null) {
            trackModuleEvent("Residents | Detail View", {
                [MixpanelProperties.TenantID]: tenantId,
                [MixpanelProperties.UserID]: globalStore.session.user?.adminId,
            })
            navigate(`/tenants/${tenantId}`)
        }
    }

    return (
        <StyledContractDetails.Tenant.Card>
            <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar size={32} name={tenant.name ?? ""} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {tenant.name ?? ""}
                    </Typography>
                </Stack>

                <StyledContractDetails.SimulateButton
                    variant="outlined"
                    onClick={handleSimulate}
                >
                    <ScreenSearchDesktopRoundedIcon sx={{ mr: 1 }} />
                    {t`contract-details.simulate-tenant`}
                </StyledContractDetails.SimulateButton>
            </Box>
            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.tenant-id`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    <StyledContractDetails.ClickableCardItemContainer
                        onClick={() => handleTenantClick(tenant.tenant_id)}
                    >
                        {tenant.tenant_id}
                    </StyledContractDetails.ClickableCardItemContainer>
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>
            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.tenant-id-external`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    {tenant.tenant_external_id}
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>
            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.personal-id`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    {tenant.personal_id}
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>
            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.email`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    {tenant.email}
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>

            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.phone-number`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    {tenant.phone_number}
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>
            <StyledContractDetails.Tenant.DetailRow>
                <StyledContractDetails.Tenant.DetailLabel variant="body2">
                    {t`contract-details.role`}
                </StyledContractDetails.Tenant.DetailLabel>
                <StyledContractDetails.Tenant.DetailValue>
                    {tenant.role}
                </StyledContractDetails.Tenant.DetailValue>
            </StyledContractDetails.Tenant.DetailRow>
        </StyledContractDetails.Tenant.Card>
    )
}

export default TenantCard
